import { PublicClientApplication, type Configuration } from '@azure/msal-browser';

export interface AuthConfig {
  enabled: boolean;
  tenant_id?: string;
  client_id?: string;
  desktop_client_id?: string;
  authority?: string;
  redirect_uri?: string;
  methods?: string[];
  native?: { enabled: boolean };
  idle_minutes?: number;
}

export interface MsalInit {
  instance: PublicClientApplication | null;
  config: AuthConfig;
}

export async function initializeMsal(): Promise<MsalInit> {
  try {
    // Fetch auth config from backend (public endpoint, no auth needed)
    const resp = await fetch('/api/auth/config');

    if (!resp.ok) {
      console.warn('Auth config fetch failed:', resp.status, resp.statusText);
      return { instance: null, config: { enabled: false } };
    }

    const contentType = resp.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
      console.warn('Auth config returned non-JSON response:', contentType);
      return { instance: null, config: { enabled: false } };
    }

    const config: AuthConfig = await resp.json();

    // If Microsoft is not configured, return the real config (it may have native auth)
    // so the app knows which methods are available.
    if (!config.client_id || !config.authority) {
      return { instance: null, config };
    }

    const appRedirectUri = config.redirect_uri || window.location.origin;
    const authRedirectUri = new URL('/redirect.html', appRedirectUri).toString();

    const msalConfig: Configuration = {
      auth: {
        clientId: config.client_id,
        authority: config.authority,
        // MSAL Browser v5 requires auth responses to land on a minimal page
        // that runs its redirect bridge. Loading the full React application
        // here leaves silent-renewal iframes waiting until they time out.
        redirectUri: authRedirectUri,
        postLogoutRedirectUri: appRedirectUri,
      },
      cache: {
        cacheLocation: 'localStorage',
      },
    };

    const instance = new PublicClientApplication(msalConfig);
    await instance.initialize();

    // Process redirect response (returns auth result if coming back from Azure AD login)
    const redirectResult = await instance.handleRedirectPromise();
    if (redirectResult?.account) {
      instance.setActiveAccount(redirectResult.account);
    }

    return { instance, config };
  } catch (err) {
    console.error('MSAL initialization failed:', err);
    return { instance: null, config: { enabled: false } };
  }
}
